import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — what Finland Oy" },
      { name: "description", content: "How what Finland Oy collects, uses, and protects your personal data in compliance with GDPR and Finnish law." },
      { property: "og:title", content: "Privacy Policy — what Finland Oy" },
      { property: "og:description", content: "How we collect, use, and protect your personal data." },
      { property: "og:url", content: "https://whatfinland.com/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://whatfinland.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#f4f6fb] text-[#0b1230]">
      <section className="relative overflow-hidden bg-[linear-gradient(180deg,#0b1230_0%,#111a44_55%,#2a3470_100%)] text-white">
        <div className="mx-auto max-w-4xl px-6 py-20 md:py-28">
          <p className="font-mono uppercase tracking-[0.28em] text-xs text-white/60 mb-4">Legal</p>
          <h1 className="font-heading text-4xl md:text-6xl leading-tight">Privacy Policy</h1>
          <p className="mt-4 text-white/70">Last updated: June 1, 2026</p>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-6 py-16 space-y-10 leading-relaxed">
        <Section title="1. Who We Are">
          <p>
            what Finland Oy ("we", "us", "our") is a company registered in Finland. We operate the what Finland
            guide and related services. For privacy questions, contact{" "}
            <a className="underline" href="mailto:privacy@whatfinland.com">privacy@whatfinland.com</a>.
          </p>
        </Section>

        <Section title="2. Data We Collect">
          <ul className="list-disc pl-6 space-y-2">
            <li>Account data: name, email, password hash, language and city preferences.</li>
            <li>Usage data: pages visited, search queries, saved places, device and browser info.</li>
            <li>Content you submit: messages, reviews, support requests.</li>
            <li>Payment data: handled by our payment processor; we do not store full card numbers.</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Data">
          <ul className="list-disc pl-6 space-y-2">
            <li>To provide and personalize the service.</li>
            <li>To process bookings, tickets, and payments.</li>
            <li>To communicate with you about your account and support requests.</li>
            <li>To improve features, security, and prevent abuse.</li>
            <li>To send marketing emails only with your consent (you can opt out anytime).</li>
          </ul>
        </Section>

        <Section title="4. Legal Basis (GDPR)">
          <p>
            We process personal data under: performance of a contract (Art. 6(1)(b)), legitimate interests
            (Art. 6(1)(f)), consent (Art. 6(1)(a)), and legal obligations (Art. 6(1)(c)).
          </p>
        </Section>

        <Section title="5. Sharing">
          <p>
            We share data with service providers (hosting, analytics, payment, email) bound by data processing
            agreements. We never sell your personal data.
          </p>
        </Section>

        <Section title="6. International Transfers">
          <p>
            Some providers operate outside the EU/EEA. Where required, transfers are protected by Standard
            Contractual Clauses or equivalent safeguards.
          </p>
        </Section>

        <Section title="7. Retention">
          <p>
            We keep account data while your account is active and for up to 24 months after deletion for legal
            and accounting purposes.
          </p>
        </Section>

        <Section title="8. Your Rights">
          <p>
            You may access, correct, delete, restrict, or port your data, and object to processing. To exercise
            these rights, email{" "}
            <a className="underline" href="mailto:privacy@whatfinland.com">privacy@whatfinland.com</a>. You may
            also lodge a complaint with the Finnish Data Protection Ombudsman (tietosuoja.fi).
          </p>
        </Section>

        <Section title="9. Cookies">
          <p>
            We use essential cookies for sign-in and preferences, and optional analytics cookies with your
            consent. You can manage cookies in your browser settings.
          </p>
        </Section>

        <Section title="10. Security">
          <p>
            We use encryption in transit, hashed passwords, access controls, and regular reviews. No system is
            100% secure; report concerns to{" "}
            <a className="underline" href="mailto:security@whatfinland.com">security@whatfinland.com</a>.
          </p>
        </Section>

        <Section title="11. Changes">
          <p>
            We may update this policy. Material changes will be notified via email or in-product notice.
          </p>
        </Section>

        <div className="pt-8 border-t border-black/10 flex flex-wrap gap-4 text-sm font-mono uppercase tracking-[0.22em]">
          <Link to="/terms" className="underline">Terms of Service</Link>
          <Link to="/contact" className="underline">Contact</Link>
        </div>
      </article>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-heading text-2xl md:text-3xl mb-3">{title}</h2>
      <div className="text-[#0b1230]/85">{children}</div>
    </section>
  );
}
